import { FC, KeyboardEventHandler } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Condition } from 'src/entities/groups';

import {
	ArrowLeftOutline,
	Box,
	Button,
	Card,
	CelebratePartyPopperOutline,
	Center,
	Display,
	DotsVerticalSolid,
	Divider,
	Flex,
	HStack,
	IconButton,
	InformationCircleOutline,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PencilAltOutline,
	rem,
	Skeleton,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	TrashOutline,
	useDisclosure,
	VStack,
} from '@bonusly/design-system';
import { useBuildRecognizeURL, useQueriesControl } from '@bonusly/url';
import { useAnalytics } from '@bonusly/who-am-i';

import { Paginator } from '../../../components/navigation/paginator';
import { PageTitle } from '../../../components/page/page-title';
import { SortableHeader } from '../../../components/table/sortable-header';
import { ClaimLimitPeriod, Incentive, IncentiveState } from '../../../entities/incentives';
import { useIncentives, useInvalidateIncentives } from '../../../queries/incentives';
import { Metadata } from '../../../queries/meta';
import { INCENTIVES_HELP_URL } from '../constants';
import { DeleteIncentiveModal } from './delete-incentive-modal';
import { AdminIncentiveTrackingEvent } from './events';

const LoadingSkeleton = () => {
	return (
		<Td>
			<Box py={2}>
				<Skeleton w="full" h={2} />
			</Box>
		</Td>
	);
};

export const IncentivesArchivedIndex = () => {
	const { t } = useTranslation('incentives', { keyPrefix: 'admin' });
	const { track } = useAnalytics();
	// The useQueryControl hooks is fine if you're only setting a single query parameter at a time
	// but in this case we potentially need to set multiple query parameters at once
	const [queries, setQueries] = useQueriesControl(['page', 'sort_by', 'sort_direction'], {
		defaultValue: {
			page: '1',
			sort_by: 'name',
			sort_direction: 'ASC',
		},
		allowedValues: {
			sort_by: ['name', 'amount'],
			sort_direction: ['ASC', 'DESC'],
		},
	});

	const parsed = parseInt(queries.page, 10);
	const page = isNaN(parsed) ? 1 : Math.max(1, parsed);

	const { data: [records, meta] = [[], new Metadata()], isLoading } = useIncentives({
		page_number: page,
		sort_by: queries.sort_by,
		sort_direction: queries.sort_direction,
		state: IncentiveState.Inactive,
	});

	return (
		<>
			<Stack px={10} py={8} spacing={6}>
				<PageTitle>{t('archived.title')}</PageTitle>
				<HStack>
					<Button as={Link} variant="link" gap={1} to="/admin/programs/incentives" type="button">
						<ArrowLeftOutline size="xs" />
						{t('archived.back')}
					</Button>
				</HStack>
				<HStack>
					<Display size="sm">{t('archived.title')}</Display>
				</HStack>

				<Card p={0} variant="flat" minW="fit-content" borderRadius="sm">
					<Table variant="simple" borderRadius="sm">
						<Thead bg="bg.soft" borderRadius="sm">
							<Tr>
								<Th minW={220} borderTopLeftRadius="sm">
									<SortableHeader
										currentDirection={queries.sort_direction}
										currentField={queries.sort_by}
										field="name"
										onSort={(field, direction) => {
											setQueries({ sort_by: field, sort_direction: direction });
											track(AdminIncentiveTrackingEvent.ArchivedSortIncentives, { field: 'name', direction: direction });
										}}
									>
										{t('nameLabel')}
									</SortableHeader>
								</Th>
								<Th minW={120}>
									<HStack gap={1} justify="flex-end">
										<SortableHeader
											currentDirection={queries.sort_direction}
											currentField={queries.sort_by}
											field="amount"
											onSort={(field, direction) => {
												setQueries({ sort_by: field, sort_direction: direction });
												track(AdminIncentiveTrackingEvent.ArchivedSortIncentives, { field: 'amount', direction: direction });
											}}
										>
											{t('pointsLabel')}
										</SortableHeader>
									</HStack>
								</Th>
								<Th minW={120}>
									<SortableHeader currentDirection={'ASC'} currentField={'period'} field="amount" onSort={() => {}}>
										{t('audienceLabel')}
									</SortableHeader>
								</Th>
								<Th minW={120}>
									<HStack gap={1} justify="flex-start" align="center">
										<Text fontSize="inherit">{t('claimLimitLabel')}</Text>
										<Tooltip label={t('claimLimitTooltip')} hasArrow>
											<Center>
												<InformationCircleOutline size="xs" />
											</Center>
										</Tooltip>
									</HStack>
								</Th>
								<Th minW={120}>
									<HStack gap={1} justify="flex-start" align="center">
										<Text fontSize="inherit">{t('limitPeriodLabel')}</Text>
										<Tooltip label={t('limitPeriodTooltip')} hasArrow>
											<Center>
												<InformationCircleOutline size="xs" />
											</Center>
										</Tooltip>
									</HStack>
								</Th>
								<Th borderTopRightRadius="sm"></Th>
							</Tr>
						</Thead>

						<Tbody>
							{records.map((incentive) => (
								<IncentiveArchivedRow key={incentive.id} incentive={incentive} />
							))}

							{isLoading &&
								Array.from({ length: 5 }).map((_, index) => (
									<Tr key={index}>
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<Td></Td>
									</Tr>
								))}
						</Tbody>
					</Table>

					{meta.totalPages > 1 && (
						<HStack p={5}>
							<Flex flexBasis={0} flexGrow={1}>
								Viewing {records.length} of {meta.totalCount} items
							</Flex>
							<Paginator
								currentPage={page}
								totalPages={meta.totalPages}
								onSelectPage={(pageNum) => {
									setQueries({ page: pageNum.toString() });
									track(AdminIncentiveTrackingEvent.ArchivedPaginate, { page: pageNum });
								}}
							/>
							<Flex flexBasis={0} flexGrow={1}></Flex>
						</HStack>
					)}
				</Card>
			</Stack>
		</>
	);
};

type IncentiveArchivedRowProps = {
	incentive: Incentive;
};

export const IncentiveArchivedRow: FC<IncentiveArchivedRowProps> = ({ incentive }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'admin' });
	const { track } = useAnalytics();
	const navigate = useNavigate();
	const recognizeURL = useBuildRecognizeURL();
	const detailPath = `/admin/programs/incentives/${incentive.id}/edit`;
	const deleteModal = useDisclosure();
	const reviewPath = recognizeURL(`/company/users/${incentive.giverId}`);
	const invalidateIncentives = useInvalidateIncentives();
	const handleViewIncentive = () => {
		navigate(detailPath);
	};

	const handleKeyDown: KeyboardEventHandler<HTMLTableRowElement> = (e) => {
		if (e.key === 'Enter') {
			handleViewIncentive();
		}
	};

	const getAudienceDisplay = (conditions: Condition[] | null): string => {
		if (!conditions) {
			return t('everyone');
		}

		return conditions.map((condition) => condition.value).join(', ');
	};

	const getClaimLimitDisplay = (incentive: Incentive): string => {
		if (incentive.claimLimit !== null) {
			return t('claimLimitPerUser', { count: incentive.claimLimit });
		} else if (incentive.quantity !== null) {
			return t('claimLimitTotal', { count: incentive.quantity });
		} else {
			return t('none');
		}
	};

	const getClaimLimitPeriodDisplay = (periodType: ClaimLimitPeriod | null): string => {
		if (periodType === null) {
			return '-';
		}

		return t(`limitPeriod.${periodType}`);
	};

	return (
		<>
			<Tr
				cursor="pointer"
				key={incentive.id}
				onClick={() => {
					navigate(detailPath);
					track(AdminIncentiveTrackingEvent.ArchivedSelectIncentive, { incentiveId: incentive.id });
				}}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				transitionProperty="background-color"
				transitionDuration="fast"
				_hover={{
					bg: 'secondary.bgAlpha',
				}}
			>
				<Td fontWeight="semibold">{incentive.name}</Td>
				<Td textAlign="end">{incentive.amount}</Td>
				<Td>{getAudienceDisplay(incentive.conditions)}</Td>
				<Td>{getClaimLimitDisplay(incentive)}</Td>
				<Td>{getClaimLimitPeriodDisplay(incentive.periodType)}</Td>
				<Td>
					<Menu>
						<MenuButton as={IconButton} aria-label={'Menu Label'} onClick={(e) => e.stopPropagation()} variant="ghost">
							<DotsVerticalSolid size="sm" />
						</MenuButton>
						<MenuList width={240}>
							<MenuItem
								as={Link}
								to={reviewPath}
								onClick={(e) => {
									track(AdminIncentiveTrackingEvent.ArchivedReviewPosts, { incentiveId: incentive.id });
									e.stopPropagation();
								}}
								py={3}
							>
								<HStack align="start" gap={1}>
									<CelebratePartyPopperOutline />
									<VStack align="start" gap={0}>
										<Text>{t('reviewLabel')}</Text>
									</VStack>
								</HStack>
							</MenuItem>
							<Divider borderColor="grayAlpha.300" borderWidth="0.5px" my={rem(1)} />
							<MenuItem
								as={Link}
								to={detailPath}
								onClick={(e) => {
									e.stopPropagation();
									track(AdminIncentiveTrackingEvent.ArchivedEditIncentive, { incentiveId: incentive.id });
								}}
								py={3}
							>
								<HStack align="start" gap={1}>
									<PencilAltOutline />
									<VStack align="start" gap={0}>
										<Text>{t('editLabel')}</Text>
									</VStack>
								</HStack>
							</MenuItem>
							<MenuItem
								as={Link}
								onClick={(e) => {
									e.stopPropagation();
									deleteModal.onOpen();
								}}
								py={3}
							>
								<HStack align="start" gap={1}>
									<TrashOutline />
									<VStack align="start" gap={0}>
										<Text>{t('deleteLabel')}</Text>
									</VStack>
								</HStack>
							</MenuItem>
						</MenuList>
					</Menu>
				</Td>
			</Tr>
			<DeleteIncentiveModal incentive={incentive} isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} onSuccess={() => invalidateIncentives()} />
		</>
	);
};
